import { useState, useCallback, useRef, useEffect } from 'react';
import { Search, X, History, Loader2, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { apiRequest } from '@/lib/queryClient';
import { format } from 'date-fns';

interface SearchResult {
  id: string;
  content: string;
  authorId: number | null;
  createdAt: string;
  author?: {
    id: number;
    username: string;
    firstName?: string | null;
    colorCode?: string | null;
  } | null;
}

interface SearchResponse {
  messages: SearchResult[];
  hasMore: boolean;
  nextCursor: string | null;
}

interface ChatSearchProps {
  channelId: string;
  onJumpToMessage: (messageId: string) => void;
  onClose: () => void;
  className?: string;
}

export function ChatSearch({ 
  channelId, 
  onJumpToMessage, 
  onClose,
  className 
}: ChatSearchProps) {
  const [query, setQuery] = useState('');
  const [includeHistory, setIncludeHistory] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const abortController = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const executeSearch = useCallback(async (
    searchQuery: string, 
    cursor?: string,
    append = false
  ) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setHasMore(false);
      setNextCursor(null);
      return;
    }

    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    if (!append) {
      setIsSearching(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const params = new URLSearchParams({
        q: searchQuery,
        limit: '25',
        includeHistory: includeHistory ? 'true' : 'false',
      });
      if (cursor) {
        params.set('cursor', cursor);
      }

      const res = await apiRequest(
        'GET',
        `/api/chat/channels/${channelId}/messages/search?${params.toString()}`
      );
      const response: SearchResponse = await res.json();

      const messages = response?.messages || [];
      if (append) {
        setResults(prev => [...prev, ...messages]);
      } else {
        setResults(messages);
      }
      setHasMore(response?.hasMore || false);
      setNextCursor(response?.nextCursor || null);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('[ChatSearch] Search error:', error);
      }
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  }, [channelId, includeHistory]);

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      executeSearch(value);
    }, 150);
  }, [executeSearch]);

  useEffect(() => {
    if (query.trim()) {
      executeSearch(query);
    }
  }, [includeHistory]);

  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  const handleLoadMore = useCallback(() => {
    if (nextCursor && !isLoadingMore) {
      executeSearch(query, nextCursor, true);
    }
  }, [nextCursor, isLoadingMore, query, executeSearch]);

  const handleResultClick = useCallback((messageId: string) => {
    onJumpToMessage(messageId);
    onClose();
  }, [onJumpToMessage, onClose]);

  const getAuthorName = (result: SearchResult): string => {
    if (!result.author) return 'Unknown';
    return result.author.firstName || result.author.username;
  };

  const highlightMatch = (text: string, searchQuery: string): React.ReactNode => {
    if (!searchQuery.trim()) return text;
    
    const terms = searchQuery.trim().split(/\s+/).filter(t => t.length > 0);
    if (terms.length === 0) return text;

    const regex = new RegExp(`(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) => {
      const isMatch = terms.some(t => part.toLowerCase() === t.toLowerCase());
      return isMatch ? (
        <span key={i} className="bg-yellow-500/30 text-yellow-200 font-medium rounded px-0.5">
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      );
    });
  };

  return (
    <div className={cn("flex flex-col bg-black/90 backdrop-blur border border-white/10 rounded-lg shadow-lg", className)}>
      <div className="flex items-center gap-2 p-2 border-b border-white/10">
        <Search className="h-4 w-4 text-white shrink-0" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search messages..."
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          className="h-7 text-xs border-0 focus-visible:ring-0 bg-transparent text-white placeholder:text-white/60"
        />
        {isSearching && <Loader2 className="h-4 w-4 animate-spin text-white shrink-0" />}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/10 text-[11px]">
        <div className="flex items-center gap-1.5">
          <Checkbox
            id="include-history"
            checked={includeHistory}
            onCheckedChange={(checked) => setIncludeHistory(!!checked)}
            className="h-3 w-3 border-white/60 data-[state=checked]:bg-white data-[state=checked]:text-background"
          />
          <label 
            htmlFor="include-history" 
            className="cursor-pointer text-white/80 hover:text-white transition-colors flex items-center gap-1"
          >
            <History className="h-3 w-3" />
            Search all history
          </label>
        </div>
        {results.length > 0 && (
          <span className="ml-auto text-white/80">
            {results.length}{hasMore ? '+' : ''} results
          </span>
        )}
      </div>

      <ScrollArea className="flex-1 max-h-[200px]">
        {results.length === 0 && query.trim() && !isSearching ? (
          <div className="flex items-center justify-center py-8 text-xs text-white/60">
            No messages found
          </div>
        ) : results.length === 0 && !query.trim() ? (
          <div className="flex items-center justify-center py-8 text-xs text-white/60">
            Type to search messages
          </div>
        ) : (
          <div className="p-1">
            {results.map((result) => (
              <button
                key={result.id}
                onClick={() => handleResultClick(result.id)}
                className="w-full text-left px-2 py-1.5 rounded hover:bg-white/10 transition-colors group"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span 
                    className="text-[11px] font-medium truncate"
                    style={{ color: result.author?.colorCode || '#ffffff' }}
                  >
                    {getAuthorName(result)}
                  </span>
                  <span className="text-[10px] text-white/60 shrink-0">
                    {format(new Date(result.createdAt), 'dd.MM HH:mm')}
                  </span>
                </div>
                <div className="text-[11px] text-white/80 group-hover:text-white line-clamp-2 mt-0.5">
                  {highlightMatch(result.content, query)}
                </div>
              </button>
            ))}

            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-7 text-[11px] mt-1"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <ChevronDown className="h-3 w-3 mr-1" />
                )}
                Load more results
              </Button>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
